import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { ContractNegotiationConcept } from '../../../models/Entities/Contracts/ContractNegotiationConcept';
import { ContractNegotiationVersion } from '../../../models/Entities/Contracts/ContractNegotiationVersion';
import { Concept } from '../../../models/Entities/Contracts/Concept';
import { useNegotiationConceptGraphStyles } from './NegotiationConceptGraph.Styles';

/** Status enum: offered=1, accepted=2, rejected=3, solicited=4 */

export interface NegotiationConceptGraphProps {
  negotiationConcepts: ContractNegotiationConcept[];
  versions: ContractNegotiationVersion[];
  allConcepts: Concept[];
}

interface ConceptSnapshot {
  id: string;
  name: string;
  reference: number;
  /** null = no version data */
  status: number | null;
  /** null = no version data */
  finalValue: number | null;
  /** ((finalValue - reference) / reference) * 100, or null if not computable */
  pctVsRef: number | null;
}

const getVersionConcept = (version: ContractNegotiationVersion, ncId: string) =>
  version.concepts?.find((c) => c.contractNegotiationConceptId === ncId);

const fmt = (value: number): string =>
  value.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });

const fmtCompact = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toFixed(0);
};

const STATUS_COLORS: Record<number, string> = {
  1: '#f9a825', // offered — yellow
  2: '#2e7d32', // accepted — green
  3: '#c62828', // rejected — red
  4: '#f9a825', // solicited — yellow
};

const NegotiationConceptGraphComponent: React.FC<NegotiationConceptGraphProps> = ({
  negotiationConcepts,
  versions,
  allConcepts,
}) => {
  const { t } = useTranslation();
  const styles = useNegotiationConceptGraphStyles();

  /** Compute a snapshot of the final/current state per concept */
  const snapshots = useMemo<ConceptSnapshot[]>(() => {
    return negotiationConcepts.map((nc) => {
      const concept = allConcepts.find((c) => c.id === nc.conceptId);
      const name = concept?.conceptName ?? String(nc.conceptId);

      // Find last version with data for this concept
      let status: number | null = null;
      let finalValue: number | null = null;

      for (let i = versions.length - 1; i >= 0; i--) {
        const vc = getVersionConcept(versions[i], nc.id);
        if (!vc) continue;
        status = vc.negotiationConceptStatus;
        if (status === 2) {
          // No counter-offer from site if siteCost is null/0 → accepted sponsor's price
          finalValue = vc.siteCost || vc.sponsorCost;
        } else if (status === 3) {
          finalValue = vc.sponsorCost;
        } else {
          finalValue = vc.siteCost;
        }
        break;
      }

      const ref = nc.negotiationReferenceCost;
      const pctVsRef =
        finalValue !== null && ref > 0
          ? ((finalValue - ref) / ref) * 100
          : null;

      return { id: nc.id, name, reference: ref, status, finalValue, pctVsRef };
    });
  }, [negotiationConcepts, versions, allConcepts]);

  /** Aggregate KPI values */
  const kpis = useMemo(() => {
    const total = snapshots.length;
    const accepted = snapshots.filter((s) => s.status === 2).length;
    const rejected = snapshots.filter((s) => s.status === 3).length;
    const inProgress = snapshots.filter((s) => s.status === 1 || s.status === 4).length;
    const noData = snapshots.filter((s) => s.status === null).length;
    const finalized = accepted + rejected;

    const pctValues = snapshots
      .filter((s) => s.pctVsRef !== null)
      .map((s) => s.pctVsRef as number);
    const avgPctVsRef =
      pctValues.length > 0
        ? pctValues.reduce((a, b) => a + b, 0) / pctValues.length
        : null;

    // Net economic impact: Σ finalValue - Σ reference (only concepts with data)
    const netImpact = snapshots
      .filter((s) => s.finalValue !== null)
      .reduce((sum, s) => sum + (s.finalValue as number) - s.reference, 0);

    const agreementRate =
      finalized > 0 ? (accepted / finalized) * 100 : null;

    return {
      total,
      accepted,
      rejected,
      inProgress,
      noData,
      finalized,
      avgPctVsRef,
      netImpact,
      agreementRate,
    };
  }, [snapshots]);

  /** Donut chart: distribution by status */
  const donutOption = useMemo(() => {
    const data = [
      { name: t('negotiationGraph.status.accepted', 'Aceptados'), value: kpis.accepted, itemStyle: { color: '#2e7d32' } },
      { name: t('negotiationGraph.status.rejected', 'Rechazados'), value: kpis.rejected, itemStyle: { color: '#c62828' } },
      { name: t('negotiationGraph.status.inProgress', 'En negociación'), value: kpis.inProgress, itemStyle: { color: '#f9a825' } },
      { name: t('negotiationGraph.status.noData', 'Sin datos'), value: kpis.noData, itemStyle: { color: '#bdbdbd' } },
    ].filter((d) => d.value > 0);

    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        bottom: 0,
        left: 'center',
        textStyle: { fontSize: 11 },
      },
      series: [
        {
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['50%', '42%'],
          avoidLabelOverlap: true,
          label: { show: false },
          emphasis: {
            label: { show: true, fontSize: 13, fontWeight: 'bold' },
          },
          data,
        },
      ],
    };
  }, [kpis, t]);

  /** Horizontal bar: value comparison vs reference per concept */
  const barOption = useMemo(() => {
    // Show at most 15 concepts, sorted by abs(pctVsRef) desc to highlight biggest deltas
    const sorted = [...snapshots]
      .filter((s) => s.reference > 0)
      .sort((a, b) => Math.abs(b.pctVsRef ?? 0) - Math.abs(a.pctVsRef ?? 0))
      .slice(0, 15)
      .reverse(); // reverse so biggest is at top in horizontal chart

    const names = sorted.map((s) => s.name);
    const refValues = sorted.map((s) => s.reference);
    const finalValues = sorted.map((s) =>
      s.finalValue !== null ? s.finalValue : s.reference
    );
    const finalColors = sorted.map((s) =>
      s.status !== null ? (STATUS_COLORS[s.status] ?? '#9e9e9e') : '#9e9e9e'
    );

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: {seriesName: string; value: number}[]) => {
          return params
            .map((p) => `${p.seriesName}: ${fmtCompact(p.value)}`)
            .join('<br/>');
        },
      },
      legend: {
        data: [
          t('negotiationGraph.bar.reference', 'Referencia'),
          t('negotiationGraph.bar.negValue', 'Valor negociado'),
        ],
        bottom: 0,
        textStyle: { fontSize: 10 },
      },
      grid: { left: '2%', right: '6%', bottom: '30px', top: '4px', containLabel: true },
      xAxis: { type: 'value', axisLabel: { formatter: (v: number) => fmtCompact(v), fontSize: 10 } },
      yAxis: {
        type: 'category',
        data: names,
        axisLabel: {
          fontSize: 10,
          formatter: (v: string) => (v.length > 18 ? `${v.slice(0, 17)}…` : v),
        },
      },
      series: [
        {
          name: t('negotiationGraph.bar.reference', 'Referencia'),
          type: 'bar',
          data: refValues,
          barGap: '20%',
          itemStyle: { color: '#bdbdbd', borderRadius: [0, 3, 3, 0] },
        },
        {
          name: t('negotiationGraph.bar.negValue', 'Valor negociado'),
          type: 'bar',
          data: finalValues.map((v, i) => ({ value: v, itemStyle: { color: finalColors[i], borderRadius: [0, 3, 3, 0] } })),
        },
      ],
    };
  }, [snapshots, t]);

  /** Line chart: avg % variation vs reference per version (negotiation trend) */
  const lineOption = useMemo(() => {
    if (versions.length === 0) return null;

    const versionLabels = versions.map((_, i) => `V${i + 1}`);

    // Per version: avg siteCost variation % vs reference for concepts with data
    const avgPctByVersion = versions.map((version) => {
      const pcts: number[] = [];
      for (const nc of negotiationConcepts) {
        const vc = getVersionConcept(version, nc.id);
        if (!vc || nc.negotiationReferenceCost <= 0) continue;
        const s = vc.negotiationConceptStatus;
        const value = s === 3 ? vc.sponsorCost
          : s === 2 ? (vc.siteCost || vc.sponsorCost)
          : vc.siteCost;
        pcts.push(((value - nc.negotiationReferenceCost) / nc.negotiationReferenceCost) * 100);
      }
      return pcts.length > 0
        ? parseFloat((pcts.reduce((a, b) => a + b, 0) / pcts.length).toFixed(2))
        : null;
    });

    // Per version: count concepts with status 2 or 3 (accumulated finalized)
    const finalizedByVersion = versions.map((version) => {
      return (version.concepts ?? []).filter(
        (vc) => vc.negotiationConceptStatus === 2 || vc.negotiationConceptStatus === 3
      ).length;
    });

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params: {seriesName: string; value: number; axisValue: string}[]) =>
          params
            .map((p) => {
              const v = p.value;
              if (p.seriesName.includes('%')) return `${p.seriesName}: ${v != null ? `${v > 0 ? '+' : ''}${v}%` : '-'}`;
              return `${p.seriesName}: ${v}`;
            })
            .join('<br/>'),
      },
      legend: {
        bottom: 0,
        textStyle: { fontSize: 10 },
      },
      grid: { left: '2%', right: '2%', bottom: '36px', top: '8px', containLabel: true },
      xAxis: {
        type: 'category',
        data: versionLabels,
        axisLabel: { fontSize: 10 },
      },
      yAxis: [
        {
          type: 'value',
          name: '%',
          nameTextStyle: { fontSize: 9 },
          axisLabel: { formatter: (v: number) => `${v > 0 ? '+' : ''}${v}%`, fontSize: 9 },
          splitLine: { lineStyle: { type: 'dashed' } },
        },
        {
          type: 'value',
          name: t('negotiationGraph.line.finalized', 'Finalizados'),
          nameTextStyle: { fontSize: 9 },
          axisLabel: { fontSize: 9 },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: t('negotiationGraph.line.avgVariation', 'Variación media %'),
          type: 'line',
          yAxisIndex: 0,
          data: avgPctByVersion,
          smooth: true,
          symbolSize: 6,
          itemStyle: { color: '#1565c0' },
          lineStyle: { color: '#1565c0', width: 2 },
          markLine: {
            silent: true,
            lineStyle: { type: 'dashed', color: '#9e9e9e' },
            data: [{ yAxis: 0, label: { formatter: '± 0%', fontSize: 9 } }],
          },
        },
        {
          name: t('negotiationGraph.line.finalizedCount', 'Conceptos finalizados'),
          type: 'bar',
          yAxisIndex: 1,
          data: finalizedByVersion,
          itemStyle: { color: 'rgba(46, 125, 50, 0.3)', borderRadius: [3, 3, 0, 0] },
          barMaxWidth: 28,
        },
      ],
    };
  }, [versions, negotiationConcepts]);

  if (negotiationConcepts.length === 0) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyState}>
          {t('negotiationGraph.empty', 'Sin conceptos de negociación para mostrar')}
        </div>
      </div>
    );
  }

  // --- KPI helpers ---
  const signedPct = (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
  const kpiProgressPct = kpis.total > 0 ? (kpis.finalized / kpis.total) * 100 : 0;

  return (
    <div className={styles.root}>
      <Text weight="semibold" size={200} className={styles.sectionTitle}>
        {t('negotiationGraph.header', 'Resumen de negociación')}
      </Text>

      {/* KPI CARDS */}
      <div className={styles.kpiRow}>
        {/* Progress */}
        <div className={styles.kpiCard}>
          <div className={styles.kpiInner}>
            <span
              className={styles.kpiAccent}
              style={{ backgroundColor: kpiProgressPct === 100 ? '#2e7d32' : '#1565c0' }}
            />
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>
                {t('negotiationGraph.kpi.progress', 'Progreso')}
              </span>
              <span className={styles.kpiValue}>
                {kpis.finalized}/{kpis.total}
              </span>
              <span className={styles.kpiSub}>
                {kpiProgressPct.toFixed(0)}%&nbsp;
                {t('negotiationGraph.kpi.finalized', 'conceptos finalizados')}
                &nbsp;·&nbsp;{kpis.inProgress}&nbsp;
                {t('negotiationGraph.kpi.inProgress', 'en curso')}
              </span>
            </div>
          </div>
        </div>

        {/* Agreement rate */}
        <div className={styles.kpiCard}>
          <div className={styles.kpiInner}>
            <span className={styles.kpiAccent} style={{ backgroundColor: '#2e7d32' }} />
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>
                {t('negotiationGraph.kpi.agreementRate', 'Tasa de acuerdo')}
              </span>
              <span className={styles.kpiValue}>
                {kpis.agreementRate !== null ? `${kpis.agreementRate.toFixed(0)}%` : '-'}
              </span>
              <span className={styles.kpiSub}>
                {kpis.accepted}&nbsp;{t('negotiationGraph.kpi.accepted', 'aceptados')}&nbsp;·&nbsp;
                {kpis.rejected}&nbsp;{t('negotiationGraph.kpi.rejected', 'rechazados')}
              </span>
            </div>
          </div>
        </div>

        {/* Avg variation vs reference */}
        <div className={styles.kpiCard}>
          <div className={styles.kpiInner}>
            <span
              className={styles.kpiAccent}
              style={{
                backgroundColor:
                  kpis.avgPctVsRef === null
                    ? '#9e9e9e'
                    : kpis.avgPctVsRef >= 0
                    ? '#2e7d32'
                    : '#c62828',
              }}
            />
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>
                {t('negotiationGraph.kpi.avgVariation', 'Variación media vs referencia')}
              </span>
              <span
                className={styles.kpiValue}
                style={{
                  color:
                    kpis.avgPctVsRef === null
                      ? undefined
                      : kpis.avgPctVsRef >= 0
                      ? '#2e7d32'
                      : '#c62828',
                }}
              >
                {kpis.avgPctVsRef !== null ? signedPct(kpis.avgPctVsRef) : '-'}
              </span>
              <span className={styles.kpiSub}>
                {t(
                  'negotiationGraph.kpi.avgVariationSub',
                  'Promedio sobre conceptos con datos'
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Net economic impact */}
        <div className={styles.kpiCard}>
          <div className={styles.kpiInner}>
            <span
              className={styles.kpiAccent}
              style={{ backgroundColor: kpis.netImpact >= 0 ? '#2e7d32' : '#c62828' }}
            />
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>
                {t('negotiationGraph.kpi.netImpact', 'Impacto económico neto')}
              </span>
              <span
                className={styles.kpiValue}
                style={{ color: kpis.netImpact >= 0 ? '#2e7d32' : '#c62828', fontSize: '20px' }}
              >
                {kpis.netImpact >= 0 ? '+' : ''}
                {fmt(kpis.netImpact)}
              </span>
              <span className={styles.kpiSub}>
                {t('negotiationGraph.kpi.netImpactSub', 'Negociado vs precio de referencia')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* CHARTS */}
      <div className={styles.chartsRow}>
        {/* Donut — status distribution */}
        <div className={styles.chartCard}>
          <Text size={200} className={styles.chartLabel}>
            {t('negotiationGraph.chart.statusDist', 'Distribución por estado')}
          </Text>
          <ReactECharts option={donutOption} style={{ height: 220 }} />
        </div>

        {/* Horizontal bar — value comparison */}
        <div className={styles.chartCard}>
          <Text size={200} className={styles.chartLabel}>
            {t('negotiationGraph.chart.valueComparison', 'Valor negociado vs referencia')}
          </Text>
          {snapshots.filter((s) => s.reference > 0).length === 0 ? (
            <div className={styles.emptyState}>
              {t('negotiationGraph.chart.noRef', 'Sin precios de referencia cargados')}
            </div>
          ) : (
            <ReactECharts option={barOption} style={{ height: 220 }} />
          )}
        </div>

        {/* Line — negotiation trend */}
        <div className={styles.chartCard}>
          <Text size={200} className={styles.chartLabel}>
            {t('negotiationGraph.chart.trend', 'Evolución por versión')}
          </Text>
          {versions.length === 0 || lineOption === null ? (
            <div className={styles.emptyState}>
              {t('negotiationGraph.chart.noVersions', 'Sin versiones de negociación')}
            </div>
          ) : (
            <ReactECharts option={lineOption} style={{ height: 220 }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default NegotiationConceptGraphComponent;
export { NegotiationConceptGraphComponent };
